import { EColorRef, IArcPrimitive, IPolyPrimitive } from '../plan-primitive';
import { EDxfLayerKind } from './dxf-layers';

/**
 * A stroke this wide on the sheet, pixels, or wider is a main line; anything thinner is thin.
 * The screen draws part outlines at 0.9 px and up (the mortise slots of a board), schema axes and
 * construction lines at 0.8 px and below.
 */
const MAIN_LINE_MIN_WIDTH_PX: number = 0.85;

/**
 * The neutral inks a dashed line is drawn in when it is an axis — the walking line, the pitch
 * line, a diagonal of a tread. A dashed line in the colour of a part (a housed tread, the opening
 * above) is an invisible outline.
 */
const AXIS_INKS: ReadonlySet<EColorRef> = new Set<EColorRef>([EColorRef.Ink3, EColorRef.Ink4, EColorRef.Warn]);

/**
 * Whether a polyline is an extension line of the screen: a faint dashed two-point line. The file
 * draws its own extension lines for every dimension, so this one is left out.
 */
export function isScreenExtensionLine(prim: IPolyPrimitive): boolean {
    const faint: boolean = (prim.strokeAlpha ?? 1) < 1;
    const dashed: boolean = (prim.dash?.length ?? 0) > 0;
    return faint && dashed && !prim.closed && prim.points.length === 2;
}

/**
 * The layer kind of a stroked line by ГОСТ 2.303: dashed in a neutral ink — an axis; dashed in
 * a part's colour — hidden; solid and at least a pixel wide — the main contour; thinner — thin.
 * The coordinate arrows of the plan are symbols, so they stay thin whatever their width.
 */
export function strokeKind(prim: IPolyPrimitive | IArcPrimitive): EDxfLayerKind {
    const stroke: EColorRef | undefined = prim.stroke;
    if (prim.dash !== undefined && prim.dash.length > 0) {
        return stroke !== undefined && AXIS_INKS.has(stroke) ? EDxfLayerKind.Axis : EDxfLayerKind.Hidden;
    }
    if (stroke === EColorRef.Axis) {
        return EDxfLayerKind.Thin;
    }
    return (prim.width ?? 1) >= MAIN_LINE_MIN_WIDTH_PX ? EDxfLayerKind.Contour : EDxfLayerKind.Thin;
}

/**
 * The layer kind of a filled shape with no stroke: an opaque fill (a wall, a post, a baluster in
 * plan) is the part itself, so its outline is a main line; a tint (a landing, the slab under the
 * opening) only shades what other lines already draw, so its outline is thin.
 */
export function fillOutlineKind(prim: IPolyPrimitive | IArcPrimitive): EDxfLayerKind {
    return prim.fillAlpha !== undefined && prim.fillAlpha < 1 ? EDxfLayerKind.Thin : EDxfLayerKind.Contour;
}
